import axios from 'axios';

const PROD_BASE_URL = 'https://api.vandigoride.com';

function getBaseURL() {
  const storedUrl = localStorage.getItem('vandigo_api_url');
  const isDev = process.env.NODE_ENV === 'development';
  return isDev ? '' : (storedUrl || PROD_BASE_URL);
}

// The admin panel is deployed as its own standalone app (separate origin
// from the backend in production), but profile pictures and other uploads
// come back from the API as paths relative to the *backend* (e.g.
// `/static/uploads/profile_pictures/x.jpg`). Used bare as an <img src>,
// the browser resolves that against the admin panel's own origin instead —
// mirrors ApiConstants.resolveMediaUrl in the Flutter app.
export function resolveMediaUrl(path) {
  if (!path) return path;
  if (/^https?:\/\//.test(path)) return path;
  const isDev = process.env.NODE_ENV === 'development';
  const base = isDev ? '' : (localStorage.getItem('vandigo_api_url') || PROD_BASE_URL);
  return path.startsWith('/') ? `${base}${path}` : `${base}/${path}`;
}

const axiosInstance = axios.create();

axiosInstance.interceptors.request.use((config) => {
  config.baseURL = getBaseURL();
  const token = localStorage.getItem('vandigo_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (process.env.NODE_ENV !== 'development') {
    config.headers['ngrok-skip-browser-warning'] = 'true';
  }
  return config;
});

function forceLogout() {
  localStorage.removeItem('vandigo_access_token');
  localStorage.removeItem('vandigo_refresh_token');
  window.location.href = '/login';
}

// A stored refresh token stays valid for days, so an expired (30-min) access
// token shouldn't end the session by itself — only a failed refresh (or an
// explicit logout, which clears the refresh token) should.
let refreshPromise = null;

function refreshAccessToken() {
  const refreshToken = localStorage.getItem('vandigo_refresh_token');
  if (!refreshToken) return Promise.reject(new Error('No refresh token available'));

  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${getBaseURL()}/api/v1/auth/refresh`, { refresh_token: refreshToken })
      .then(({ data }) => {
        localStorage.setItem('vandigo_access_token', data.access_token);
        localStorage.setItem('vandigo_refresh_token', data.refresh_token);
        return data.access_token;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const isAuthEndpoint = config?.url?.includes('/auth/');

    // A response blocked by CORS (or otherwise stripped in transit) looks
    // identical to a plain network error to axios — no `response` at all —
    // even when the server actually sent a 401 (seen in prod: an expired
    // admin session's 401 came back without CORS headers, so this handler
    // never saw status 401 and the UI just retried the same request
    // forever instead of refreshing/logging out). A real offline drop is
    // the other common cause of a response-less error, so only treat it as
    // a masked auth failure when the browser still thinks it's online and
    // we believe we're logged in.
    const isMaskedAuthFailure =
      !response && navigator.onLine && !!localStorage.getItem('vandigo_access_token');
    const isAuthFailure = response?.status === 401 || isMaskedAuthFailure;

    if (isAuthFailure && config && !isAuthEndpoint && !config._retry) {
      config._retry = true;
      try {
        const newToken = await refreshAccessToken();
        config.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(config);
      } catch {
        forceLogout();
        return Promise.reject(error);
      }
    }

    // Excludes /auth/ endpoints here too — a failed login or refresh call
    // returning 401 is an expected, normal failure the caller already
    // handles (Login.jsx shows "Invalid credentials"; the refresh-retry
    // block above already calls forceLogout() when a refresh genuinely
    // fails). Without this check, a wrong password on the login page itself
    // triggered a full-page forceLogout() redirect that wiped the error
    // toast before it could ever be seen.
    if (isAuthFailure && !isAuthEndpoint) {
      forceLogout();
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
